import React, { useEffect, useState } from "react";
import { Button, Drawer, Empty, Input, Progress, Spin, Table, message } from "antd";
import { Compass, Download, RefreshCw, Upload } from "lucide-react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BRAND, DARK } from "theme/tokens";
import {
  WELCOME_REVIEWS_DRAWER_SHARED_PROPS,
  welcomeReviewsDrawerWidth,
} from "pages/Welcome/welcomeReviewsDrawerProps";
import {
  ANALYSIS_UI_STEPS,
  analysisUiStepIndex,
  canSaveDiscoveredAudience,
  ingestSnapshotReady,
  displayAudienceName,
  estimateDiscoverySeconds,
  isDiscoveryAnalyzing,
  isDiscoveryFailed,
  isDiscoveryReady,
  remainingDiscoverySeconds,
} from "./exploreWhy";
import ExplainabilityDetail from "./ExplainabilityDetail";
import {
  decisionSummary,
  memberCellValue,
  memberColumns,
  memberWhyText,
} from "./exploreExplainability";
import { inspectDiscoveredAudience } from "./service";
import styles from "./ExplorePanel.module.scss";

const MAX_BYTES = 25 * 1024 * 1024;
const ACCEPTED =
  ".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const formatCount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString();
};

const csvEscape = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const downloadDiscoveredCsv = (snapshot, rows, t) => {
  const headers = [
    t("abs.explore_col_audience", "Audience"),
    t("abs.explore_col_people", "People"),
    t("abs.explore_col_share", "Share %"),
    t("abs.explore_col_why", "Why it matters"),
  ];
  const body = rows.map((row) =>
    [
      displayAudienceName(row.name),
      row.size,
      row.share,
      row.why,
    ]
      .map(csvEscape)
      .join(",")
  );
  const blob = new Blob([[headers.join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const slug = String(snapshot?.filename || "explore")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  link.download = `${slug || "explore"}-discovered-audiences.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const SliceCharts = ({ slices, t, scoped }) => (
  <div className={styles.charts}>
    <BarChart
      title={t("abs.explore_chart_category", "Events by category")}
      caption={
        scoped
          ? t("abs.explore_chart_category_audience", "Last product group · this audience")
          : t("abs.explore_chart_category_cap", "Last product group per person")
      }
      rows={slices?.categories}
    />
    <BarChart
      title={t("abs.explore_chart_location", "Last location")}
      caption={
        scoped
          ? t("abs.explore_chart_location_audience", "Unique profiles · last event location")
          : t("abs.explore_chart_location_cap", "Unique profiles · last event location")
      }
      rows={slices?.locations}
    />
    <BarChart
      title={t("abs.explore_chart_recency", "Recency")}
      caption={t("abs.explore_chart_recency_cap", "Days since last event")}
      rows={slices?.recency}
    />
    <BarChart
      title={t("abs.explore_chart_channel", "Channel")}
      caption={
        scoped
          ? t("abs.explore_chart_channel_audience", "Unique profiles · last channel")
          : t("abs.explore_chart_channel_cap", "Unique profiles · last channel")
      }
      rows={slices?.channels}
    />
  </div>
);

const BarChart = ({ title, caption, rows }) => {
  const max = Math.max(1, ...(rows || []).map((row) => Number(row.value) || 0));
  if (!rows || !rows.length) return null;
  return (
    <div className={styles.panel}>
      <h2>{title}</h2>
      {caption ? <p className={styles.cap}>{caption}</p> : null}
      {(rows || []).map((row) => (
        <div className={styles.barRow} key={row.label}>
          <span className={styles.barLabel} title={row.label}>
            {row.label}
          </span>
          <div className={styles.track}>
            <i style={{ width: `${Math.max(4, (100 * (Number(row.value) || 0)) / max)}%` }} />
          </div>
          <b>{formatCount(row.value)}</b>
        </div>
      ))}
    </div>
  );
};

const ExplorePanel = ({
  analysis,
  loading = false,
  uploading,
  uploadProgress = 0,
  uploadFileName = "",
  savingKey,
  savedNames,
  liveCount,
  refreshing = false,
  onRefresh,
  onUpload,
  onSave,
  onRunFromScratch,
}) => {
  const { t } = useTranslation();
  const theme = useSelector((state) => state.DashboardReducer.theme) || "light";
  const [dragOver, setDragOver] = useState(false);
  const [selected, setSelected] = useState(null);
  const [inspect, setInspect] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [rawMember, setRawMember] = useState(null);
  const [memberQuery, setMemberQuery] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const snapshot = analysis?.snapshot;
  const status = analysis?.status || "idle";
  const discovering = isDiscoveryAnalyzing(analysis);
  const discoveryFailed = isDiscoveryFailed(analysis);
  const ready = status === "ready" && snapshot && isDiscoveryReady(analysis);
  const discovered = Array.isArray(analysis?.discovered_audiences)
    ? analysis.discovered_audiences
    : [];

  const handleFile = (file) => {
    if (!file) return;
    const name = String(file.name || "").toLowerCase();
    const okType =
      name.endsWith(".csv") || name.endsWith(".xlsx") || name.endsWith(".xls");
    if (!okType) {
      message.error(t("abs.explore_file_type", "Please choose a CSV or Excel file"));
      return;
    }
    if (file.size > MAX_BYTES) {
      message.error(t("abs.explore_file_too_large", "File is too large. Maximum is 25 MB."));
      return;
    }
    onUpload(file);
  };

  const dropzone = (
    <label
      className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ""}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragOver(false);
        handleFile(event.dataTransfer?.files?.[0]);
      }}
    >
      <Upload size={20} strokeWidth={2} aria-hidden="true" />
      {t("abs.explore_drop", "Drop a CSV or Excel file, or click to browse")}
      <span className={styles.dropHint}>
        {t(
          "abs.explore_drop_body",
          "Upload your customer sales data. Pivony analyzes behavioral patterns in the file and discovers meaningful customer audiences."
        )}
      </span>
      <span className={styles.dropHint}>
        {t(
          "abs.explore_limits",
          "Maximum 25 MB or 100,000 rows. Replaces the previous analysis."
        )}
      </span>
      <input
        type="file"
        accept={ACCEPTED}
        className={styles.fileInput}
        disabled={uploading}
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
    </label>
  );

  const failedCopy =
    analysis?.error_code === "ABS_ANALYSIS_TOO_LARGE"
      ? t(
          "abs.explore_gateway_too_large",
          "This file is too large for the upload gateway. Maximum is 25 MB."
        )
      : t(
          "abs.explore_failed",
          "Could not analyze this file. Check columns and try a smaller extract."
        );
  const busy = uploading || status === "processing" || discovering;
  const bootLoading = loading && !ready && !busy;
  const eventCount = snapshot?.event_count || 0;
  const expectedSec = estimateDiscoverySeconds(eventCount);
  const remainingSec = remainingDiscoverySeconds({
    startedAt: analysis?.discovery_job?.started_at,
    createdAt: analysis?.discovery_job?.created_at,
    eventCount,
    nowMs,
  });
  const typicalMinutes = Math.max(1, Math.round(expectedSec / 60));
  const etaCopy =
    remainingSec > 90
      ? t(
          "abs.explore_eta_minutes",
          "Files this size usually take about {{typical}} minutes. About {{left}} minutes left.",
          {
            typical: typicalMinutes,
            left: Math.max(1, Math.round(remainingSec / 60)),
          }
        )
      : remainingSec > 0
        ? t("abs.explore_eta_soon", "Usually under a minute left.")
        : t(
            "abs.explore_eta_overtime",
            "Taking longer than usual. This often finishes within a few more minutes."
          );
  const percent = Math.min(99, Math.max(1, Number(uploadProgress) || 1));
  const uploadingBytes = uploading && percent < 90;
  const ingestReady = ingestSnapshotReady(analysis);
  const uiStep = uploadingBytes ? -1 : analysisUiStepIndex(analysis);
  const stepLabels = {
    profiles: t("abs.explore_step_profiles", "Building customer profiles"),
    patterns: t("abs.explore_step_patterns", "Looking for behavioral patterns"),
    audiences: t("abs.explore_step_audiences", "Finding distinct customer audiences"),
    verify: t("abs.explore_step_verify", "Confirming audiences"),
  };

  const isSaved = (row) => savedNames.has(String(row.name || "").trim().toLowerCase());
  const closeDetail = () => {
    setSelected(null);
    setInspect(null);
    setMembersOpen(false);
    setRawMember(null);
    setMemberQuery("");
  };
  const selectedName = selected ? displayAudienceName(selected.name) : "";
  const behaviorColumns = selected
    ? memberColumns((inspect || selected).explainability_evidence)
    : [];
  const memberRows = Array.isArray(inspect?.members) ? inspect.members : [];
  const visibleMembers = memberQuery.trim()
    ? memberRows.filter((row) => {
        const q = memberQuery.trim().toLowerCase();
        return (
          String(row.email || "").toLowerCase().includes(q) ||
          String(row.display_name || "").toLowerCase().includes(q)
        );
      })
    : memberRows;

  useEffect(() => {
    if (!selected?.id) {
      setInspect(null);
      return undefined;
    }
    let cancelled = false;
    setInspectLoading(true);
    inspectDiscoveredAudience(selected.id).then((res) => {
      if (cancelled) return;
      setInspectLoading(false);
      if (!res || res.error || res.database_ready === false) {
        setInspect(null);
        return;
      }
      setInspect(res);
    });
    return () => {
      cancelled = true;
    };
  }, [selected?.id]);

  useEffect(() => {
    if (!busy || uploadingBytes) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [busy, uploadingBytes]);

  return (
    <div className={styles.explore}>
      {bootLoading ? (
        <div className={styles.analyzing} role="status" aria-live="polite">
          <Spin size="large" />
          <h2 className={styles.analyzingTitle}>
            {t("abs.explore_loading_title", "Loading your analysis")}
          </h2>
          <p className={styles.analyzingCopy}>
            {t(
              "abs.explore_loading_copy",
              "Fetching profiles, events, and discovered audiences."
            )}
          </p>
        </div>
      ) : null}
      {busy ? (
        <div className={styles.analyzing} role="status" aria-live="polite">
          <h2 className={styles.analyzingTitle}>
            {ingestReady
              ? t("abs.explore_discovering_title", "Discovering audiences")
              : t("abs.explore_analyzing_title", "Analyzing your audience")}
          </h2>
          {uploadingBytes ? (
            <>
              <Progress
                percent={percent}
                size="small"
                strokeColor={theme === "dark" ? DARK.textSecondary : BRAND.primary}
                trailColor={theme === "dark" ? DARK.bgSubtle : undefined}
                className={styles.progressBar}
              />
              <p className={styles.analyzingCopy}>
                {t("abs.explore_uploading", "Uploading {{name}}… {{percent}}%", {
                  name: uploadFileName || t("abs.explore_file", "file"),
                  percent,
                })}
              </p>
            </>
          ) : (
            <>
              <p className={styles.analyzingCopy}>
                {ingestReady
                  ? t(
                      "abs.explore_profiles_ready",
                      "Customer profiles are already identified. Finding distinct audiences in this file."
                    )
                  : t(
                      "abs.explore_processing",
                      "Reading the file and discovering customer audiences."
                    )}
              </p>
              {eventCount > 0 ? (
                <p className={styles.analyzingEta}>{etaCopy}</p>
              ) : (
                <p className={styles.analyzingEta}>
                  {t(
                    "abs.explore_eta_generic",
                    "Discovery usually takes a few minutes. This page updates automatically."
                  )}
                </p>
              )}
            </>
          )}
          <ol className={styles.steps}>
            {ANALYSIS_UI_STEPS.map((key, index) => {
              const done = uiStep > index;
              const current = uiStep === index;
              return (
                <li
                  key={key}
                  className={`${styles.step} ${done ? styles.stepDone : ""} ${
                    current ? styles.stepCurrent : ""
                  }`}
                >
                  <span className={styles.stepMark} aria-hidden="true">
                    {done ? "✓" : "○"}
                  </span>
                  {stepLabels[key]}
                </li>
              );
            })}
          </ol>
          {onRefresh ? (
            <Button
              type="link"
              icon={<RefreshCw size={14} strokeWidth={2} />}
              loading={refreshing}
              onClick={onRefresh}
            >
              {t("abs.refresh_status", "Refresh status")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {!busy && !bootLoading ? dropzone : null}

      {status === "failed" && !busy ? (
        <div className={styles.failed} role="alert">
          <p>{failedCopy}</p>
          <p className={styles.failedHint}>
            {t("abs.explore_failed_retry", "Choose a different file to try again.")}
          </p>
        </div>
      ) : null}

      {discoveryFailed && !busy && status !== "failed" ? (
        <div className={styles.failed} role="alert">
          <p>
            {t(
              "abs.explore_discovery_failed",
              "Customer audience discovery could not be completed for this file."
            )}
          </p>
          <p className={styles.failedHint}>
            {t("abs.explore_failed_retry", "Choose a different file to try again.")}
          </p>
        </div>
      ) : null}

      {ready ? (
        <>
          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <b>{formatCount(snapshot.profile_count)}</b>
              <span>{t("abs.explore_kpi_profiles", "Identified profiles")}</span>
            </div>
            <div className={styles.kpi}>
              <b>{formatCount(snapshot.event_count)}</b>
              <span>{t("abs.explore_kpi_events", "Events in window")}</span>
            </div>
            <div className={styles.kpi}>
              <b>{formatCount(discovered.length)}</b>
              <span>{t("abs.explore_kpi_audiences", "Discovered audiences")}</span>
            </div>
            <div className={styles.kpi}>
              <b>{formatCount(liveCount)}</b>
              <span>{t("abs.explore_kpi_live", "Saved audiences")}</span>
            </div>
          </div>
          <div className={styles.fileRow}>
            {snapshot.filename ? (
              <p className={styles.fileMeta}>
                {t("abs.explore_from", "From {{name}}", { name: snapshot.filename })}
              </p>
            ) : (
              <span />
            )}
            <Button
              type="link"
              danger
              size="small"
              icon={<RefreshCw size={14} strokeWidth={2} />}
              onClick={onRunFromScratch}
            >
              {t("abs.explore_rerun", "Run from scratch")}
            </Button>
          </div>
          {snapshot.dropped_hubs > 0 ? (
            <div className={styles.note}>
              <strong>{t("abs.explore_identity_on", "Identity filter on.")}</strong>{" "}
              {t(
                "abs.explore_identity_body",
                "Agency / operator IDs are excluded from Explore ({{hubs}} hub(s), {{sales}} sales). Audiences below are consumer channels only.",
                {
                  hubs: snapshot.dropped_hubs,
                  sales: snapshot.dropped_hub_sales,
                }
              )}
            </div>
          ) : null}

          {selected ? (
            <div className={styles.deepDive}>
              <p className={styles.crumb}>
                <button type="button" className={styles.crumbBtn} onClick={closeDetail}>
                  {t("abs.explore_discovered", "Discovered audiences")}
                </button>
                <span> / {selectedName}</span>
              </p>
              <div className={styles.deepHead}>
                <div>
                  <h2 className={styles.deepTitle}>{selectedName}</h2>
                  <p className={styles.detailMeta}>
                    {t("abs.explore_people_count", "{{count}} customers", {
                      count: formatCount(selected.size),
                    })}
                    {selected.share != null && selected.share !== ""
                      ? ` · ${selected.share}%`
                      : ""}
                  </p>
                </div>
                <div className={styles.deepActions}>
                  <Button onClick={closeDetail}>{t("abs.explore_back", "Back")}</Button>
                  {isSaved(selected) ? (
                    <span className={styles.savedMark}>{t("abs.explore_saved", "Saved")}</span>
                  ) : (
                    <Button
                      type="primary"
                      loading={savingKey === selected.id}
                      disabled={
                        !canSaveDiscoveredAudience(selected) || savingKey === selected.id
                      }
                      onClick={() => {
                        if (canSaveDiscoveredAudience(selected)) onSave(selected);
                      }}
                    >
                      {t("abs.explore_save_audience", "Save audience")}
                    </Button>
                  )}
                </div>
              </div>
              <ExplainabilityDetail
                audience={selected}
                inspect={inspect}
                t={t}
                formatCount={formatCount}
                members={memberRows}
                onOpenMembers={() => setMembersOpen(true)}
              />
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>
                  {t("abs.explore_slices_audience", "Slices for this audience")}
                </h2>
              </div>
              <p className={styles.sectionCap}>
                {t(
                  "abs.explore_slices_audience_cap",
                  "Location, product, and channel among these customers — not the whole file."
                )}
              </p>
              {inspectLoading ? (
                <div className={styles.inspectLoading} role="status">
                  <Spin />
                </div>
              ) : inspect && inspect.inspectable === false ? (
                <p className={styles.sectionCap}>
                  {t(
                    "abs.explore_slices_unavailable",
                    "Membership for this audience cannot be replayed yet, so charts and rows are unavailable."
                  )}
                </p>
              ) : (
                <SliceCharts slices={inspect?.slices} t={t} scoped />
              )}
            </div>
          ) : (
            <>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>
              {t("abs.explore_discovered", "Discovered audiences")}
            </h2>
            <Button
              type="link"
              size="small"
              icon={<Download size={14} strokeWidth={2} />}
              disabled={!discovered.length}
              onClick={() => downloadDiscoveredCsv(snapshot, discovered, t)}
            >
              {t("abs.download_csv", "Download CSV")}
            </Button>
          </div>
          <p className={styles.sectionCap}>
            {t(
              "abs.explore_discovered_cap",
              "Stand-out customer audiences from behavioral patterns in this file."
            )}
          </p>
          {discovered.length ? (
            <p className={styles.countLine}>
              {t("abs.explore_found_count", "{{count}} audiences discovered", {
                count: discovered.length,
              })}
            </p>
          ) : null}
          {discovered.length ? (
            <div className={styles.cardGrid}>
              {discovered.map((row) => {
                const saved = isSaved(row);
                const activatable = canSaveDiscoveredAudience(row);
                const title = displayAudienceName(row.name);
                return (
                  <article className={styles.audienceCard} key={row.id}>
                    <span className={`${styles.origin} ${styles.originDiscovered}`}>
                      {t("abs.origin_discovered", "Discovered")}
                    </span>
                    <h3 className={styles.cardTitle}>{title}</h3>
                    <p className={styles.cardMeta}>
                      {t("abs.explore_people_count", "{{count}} customers", {
                        count: formatCount(row.size),
                      })}
                      {row.share != null && row.share !== "" ? ` · ${row.share}%` : ""}
                    </p>
                    {decisionSummary(row) ? (
                      <p className={styles.cardDecision}>{decisionSummary(row)}</p>
                    ) : row.why ? (
                      <p className={styles.cardWhy}>{row.why}</p>
                    ) : null}
                    <div className={styles.cardActions}>
                      <Button type="primary" onClick={() => setSelected(row)}>
                        {t("abs.explore_inspect", "Inspect")}
                      </Button>
                      {saved ? (
                        <span className={styles.savedMark}>
                          {t("abs.explore_saved", "Saved")}
                        </span>
                      ) : (
                        <Button
                          disabled={!activatable || savingKey === row.id}
                          loading={savingKey === row.id}
                          onClick={() => {
                            if (activatable) onSave(row);
                          }}
                        >
                          {t("abs.explore_save", "Save")}
                        </Button>
                      )}
                    </div>
                    {!saved && !activatable ? (
                      <p className={styles.inactiveHint}>
                        {t(
                          "abs.explore_not_activatable",
                          "This audience cannot be activated directly yet."
                        )}
                      </p>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : (
            <Empty
              image={<Compass size={36} strokeWidth={1.5} />}
              description={t(
                "abs.explore_no_discovered",
                "No distinct customer pattern stood out in this file."
              )}
            />
          )}

          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>
              {t("abs.explore_slices", "Slices")}
            </h2>
          </div>
          <p className={styles.sectionCap}>
            {t(
              "abs.explore_slices_cap",
              "Location, product, and channel charts. Context for the file — not how audiences were discovered."
            )}
          </p>
          <SliceCharts slices={snapshot} t={t} scoped={false} />
            </>
          )}
        </>
      ) : null}

      {!ready && !busy && !bootLoading && status !== "failed" && !discoveryFailed ? (
        <Empty
          image={<Compass size={36} strokeWidth={1.5} />}
          description={t(
            "abs.explore_empty",
            "Upload a sales file. Discovered audiences appear after analysis."
          )}
        />
      ) : null}

      <Drawer
        {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
        title={t("abs.explore_members_title", "Customers in this audience")}
        extra={
          <button
            type="button"
            className="welcome-reviews-drawer-header-close"
            onClick={() => setMembersOpen(false)}
          >
            {t("common.close", "Close")}
          </button>
        }
        onClose={() => {
          setMembersOpen(false);
          setRawMember(null);
        }}
        open={Boolean(selected) && membersOpen}
        width={Math.max(welcomeReviewsDrawerWidth(), 640)}
      >
        {selected ? (
          <div className={styles.membersPane}>
            <p className={styles.detailMeta}>
              {t("abs.explore_members_count", "{{shown}} of {{total}} rows", {
                shown: visibleMembers.length,
                total: formatCount(inspect?.total ?? selected.size),
              })}
              {inspect?.truncated
                ? ` · ${t("abs.members_truncated", "showing first {{count}}", {
                    count: memberRows.length,
                  })}`
                : ""}
            </p>
            <Input
              allowClear
              value={memberQuery}
              onChange={(event) => setMemberQuery(event.target.value)}
              placeholder={t("abs.explore_members_search", "Search email or name")}
              className={styles.memberSearch}
            />
            <Table
              size="small"
              rowKey={(row) => row.signal_profile_id || row.email}
              loading={inspectLoading}
              columns={[
                {
                  title: t("abs.col_name", "Name"),
                  dataIndex: "display_name",
                  key: "display_name",
                  ellipsis: true,
                  render: (value, row) => value || row.email || "—",
                },
                ...behaviorColumns.map((column) => ({
                  title: column.label,
                  key: column.key,
                  render: (_, row) => memberCellValue(row, column) || "—",
                })),
                {
                  title: t("abs.explore_why_included", "Why included"),
                  key: "why_included",
                  render: (_, row) => memberWhyText(row.why_included) || "—",
                },
                {
                  title: t("abs.explore_raw_record", "Raw record"),
                  key: "raw",
                  width: 120,
                  render: (_, row) => (
                    <Button type="link" size="small" onClick={() => setRawMember(row)}>
                      {t("abs.explore_view_raw", "View raw record")}
                    </Button>
                  ),
                },
              ]}
              dataSource={visibleMembers}
              pagination={{ pageSize: 20, hideOnSinglePage: true }}
              scroll={{ x: true }}
            />
            {rawMember ? (
              <dl className={styles.rawRecord}>
                <div>
                  <dt>{t("abs.col_email", "Email")}</dt>
                  <dd>{rawMember.email || "—"}</dd>
                </div>
                <div>
                  <dt>{t("abs.col_name", "Name")}</dt>
                  <dd>{rawMember.display_name || "—"}</dd>
                </div>
                <div>
                  <dt>{t("abs.col_location", "Last location")}</dt>
                  <dd>{rawMember.location || "—"}</dd>
                </div>
                <div>
                  <dt>{t("abs.col_product", "Product")}</dt>
                  <dd>{rawMember.product_group || "—"}</dd>
                </div>
                <div>
                  <dt>{t("abs.col_party", "Party")}</dt>
                  <dd>{rawMember.pax ?? "—"}</dd>
                </div>
                <div>
                  <dt>{t("abs.col_last", "Last event")}</dt>
                  <dd>
                    {rawMember.last_event_at
                      ? String(rawMember.last_event_at).replace("T", " ").slice(0, 16)
                      : "—"}
                  </dd>
                </div>
              </dl>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </div>
  );
};

export default ExplorePanel;
